import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Globe,
  Upload,
  Building,
  Eye,
  Loader2,
  Check,
  X,
  ExternalLink,
  Trash2,
  Image
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { whitelabelService, WhitelabelConfig } from '../../services/whitelabelService';
import api from '../../services/api';

const WhitelabelSettings: React.FC = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState<WhitelabelConfig | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    domain: '',
    subdomain: '',
    logo_url: '',
    favicon_url: '',
    company_name: '',
    is_active: true
  });

  const [availability, setAvailability] = useState({
    domain_available: true,
    subdomain_available: true,
    checking: false
  });

  const baseDomain = process.env.REACT_APP_BASE_DOMAIN || 'devskin.com';

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setIsLoading(true);
    try {
      const response = await whitelabelService.getConfig();
      if (response.success && response.data) {
        setConfig(response.data);
        setFormData({
          domain: response.data.domain || '',
          subdomain: response.data.subdomain || '',
          logo_url: response.data.logo_url || '',
          favicon_url: response.data.favicon_url || '',
          company_name: response.data.company_name || '',
          is_active: response.data.is_active
        });
      }
    } catch (error) {
      console.error('Error loading whitelabel config:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (file: File, type: 'logo' | 'favicon') => {
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingFavicon;
    const field = type === 'logo' ? 'logo_url' : 'favicon_url';

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await api.post('/upload/image', formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data.success && response.data.url) {
        setFormData(prev => ({ ...prev, [field]: response.data.url }));
        toast.success(t('whitelabel.upload_success'));
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || t('whitelabel.upload_error'));
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'favicon') => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(t('whitelabel.invalid_file_type'));
        return;
      }
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('whitelabel.file_too_large'));
        return;
      }
      handleUpload(file, type);
    }
  };

  const checkAvailability = async (field: 'domain' | 'subdomain', value: string) => {
    if (!value) return;

    setAvailability(prev => ({ ...prev, checking: true }));
    try {
      const params = field === 'domain' ? { domain: value } : { subdomain: value };
      const response = await whitelabelService.checkAvailability(params.domain, params.subdomain);
      if (response.success && response.data) {
        const data = response.data;
        setAvailability(prev => ({
          ...prev,
          [`${field}_available`]: data[`${field}_available` as keyof typeof data],
          checking: false
        }));
      }
    } catch (error) {
      setAvailability(prev => ({ ...prev, checking: false }));
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Clean up empty strings - only send non-empty values
      const dataToSend: Record<string, any> = {
        is_active: formData.is_active
      };

      if (formData.domain) dataToSend.domain = formData.domain;
      if (formData.subdomain) dataToSend.subdomain = formData.subdomain;
      if (formData.logo_url) dataToSend.logo_url = formData.logo_url;
      if (formData.favicon_url) dataToSend.favicon_url = formData.favicon_url;
      if (formData.company_name) dataToSend.company_name = formData.company_name;

      const response = await whitelabelService.saveConfig(dataToSend);
      if (response.success && response.data) {
        setConfig(response.data);
        toast.success(t('whitelabel.save_success'));
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('whitelabel.save_error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(t('whitelabel.confirm_delete'))) return;

    try {
      const response = await whitelabelService.deleteConfig();
      if (response.success) {
        setConfig(null);
        setFormData({
          domain: '',
          subdomain: '',
          logo_url: '',
          favicon_url: '',
          company_name: '',
          is_active: true
        });
        toast.success(t('whitelabel.delete_success'));
      }
    } catch (error) {
      toast.error(t('whitelabel.delete_error'));
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-3 sm:space-y-6">
      {/* Domain Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="w-5 h-5" />
            {t('whitelabel.domain_config')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('whitelabel.domain_label')}
              </label>
              <Input
                value={formData.domain || formData.subdomain}
                onChange={(e) => {
                  const value = e.target.value.toLowerCase().trim();
                  // If it looks like a full domain, store in domain field
                  if (value.includes('.') && !value.endsWith(`.${baseDomain}`)) {
                    setFormData({ ...formData, domain: value, subdomain: '' });
                  } else {
                    // Otherwise treat as subdomain
                    const cleanValue = value.replace(/[^a-z0-9-]/g, '');
                    setFormData({ ...formData, subdomain: cleanValue, domain: '' });
                  }
                }}
                placeholder={t('whitelabel.domain_placeholder')}
                className="max-w-md"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                {t('whitelabel.domain_examples')}
              </p>
            </div>

            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
              <p className="text-sm text-blue-800 dark:text-blue-200">
                <strong>{t('whitelabel.dns_config_title')}</strong>
              </p>
              <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                {t('whitelabel.dns_config_desc')}
              </p>
              <a
                href="mailto:contato@devskin.com?subject=Configuração de Whitelabel"
                className="inline-flex items-center gap-2 mt-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
              >
                contato@devskin.com
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Branding Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            {t('whitelabel.branding')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Building className="w-4 h-4 inline mr-1" />
                {t('whitelabel.company_name')}
              </label>
              <Input
                value={formData.company_name}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                placeholder={t('whitelabel.company_name_placeholder')}
                className="max-w-md"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Upload className="w-4 h-4 inline mr-1" />
                  {t('whitelabel.logo')}
                </label>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'logo')}
                  className="hidden"
                />
                <div className="flex items-center gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => logoInputRef.current?.click()}
                    isLoading={uploadingLogo}
                    leftIcon={<Upload className="w-4 h-4" />}
                  >
                    {formData.logo_url ? t('whitelabel.change_logo') : t('whitelabel.upload_logo')}
                  </Button>
                  {formData.logo_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, logo_url: '' })}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
                {formData.logo_url && (
                  <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg inline-block">
                    <img
                      src={formData.logo_url}
                      alt="Logo preview"
                      className="h-16 object-contain"
                      onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {t('whitelabel.logo_hint')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Upload className="w-4 h-4 inline mr-1" />
                  {t('whitelabel.favicon')}
                </label>
                <input
                  ref={faviconInputRef}
                  type="file"
                  accept="image/*,.ico"
                  onChange={(e) => handleFileChange(e, 'favicon')}
                  className="hidden"
                />
                <div className="flex items-center gap-3">
                  <Button
                    variant="secondary"
                    onClick={() => faviconInputRef.current?.click()}
                    isLoading={uploadingFavicon}
                    leftIcon={<Upload className="w-4 h-4" />}
                  >
                    {formData.favicon_url ? t('whitelabel.change_favicon') : t('whitelabel.upload_favicon')}
                  </Button>
                  {formData.favicon_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setFormData({ ...formData, favicon_url: '' })}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
                {formData.favicon_url && (
                  <div className="mt-3 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg inline-block">
                    <img
                      src={formData.favicon_url}
                      alt="Favicon preview"
                      className="w-10 h-10 object-contain"
                      onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {t('whitelabel.favicon_hint')}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Eye className="w-5 h-5" />
              {t('whitelabel.preview')}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
            >
              {showPreview ? t('common.hide') : t('common.show')}
            </Button>
          </CardTitle>
        </CardHeader>
        {showPreview && (
          <CardContent>
            <div className="border rounded-xl overflow-hidden">
              <div className="p-6 text-center bg-gradient-to-br from-primary-50 to-white dark:from-gray-800 dark:to-gray-900">
                {formData.logo_url ? (
                  <img
                    src={formData.logo_url}
                    alt="Logo"
                    className="h-16 mx-auto mb-4 object-contain"
                  />
                ) : (
                  <div className="w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center bg-primary-600 text-white text-2xl font-bold">
                    {formData.company_name?.charAt(0) || 'A'}
                  </div>
                )}
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  {formData.company_name || t('whitelabel.preview_company')}
                </h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                  {formData.subdomain ? `${formData.subdomain}.${baseDomain}` : formData.domain || 'seu-dominio.com.br'}
                </p>
                <button className="mt-4 px-6 py-2 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 transition-colors">
                  {t('auth.login')}
                </button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Active Toggle & Actions */}
      <Card>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 dark:after:border-gray-600 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  {t('whitelabel.enable_whitelabel')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('whitelabel.enable_whitelabel_help')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {config && (
                <Button
                  variant="danger"
                  onClick={handleDelete}
                  leftIcon={<Trash2 className="w-4 h-4" />}
                >
                  {t('common.delete')}
                </Button>
              )}
              <Button
                variant="primary"
                onClick={handleSave}
                isLoading={isSaving}
              >
                {t('common.save')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* URL Info */}
      {config && config.is_active && (formData.subdomain || formData.domain) && (
        <Card>
          <CardContent>
            <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-xl">
              <div>
                <p className="font-medium text-green-800 dark:text-green-200">
                  {t('whitelabel.active_url')}
                </p>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {formData.domain || `${formData.subdomain}.${baseDomain}`}
                </p>
              </div>
              <a
                href={`https://${formData.domain || `${formData.subdomain}.${baseDomain}`}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-green-700 dark:text-green-300 hover:underline"
              >
                {t('whitelabel.visit')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default WhitelabelSettings;
